import { AlertTriangle } from "lucide-react"
import { Component, type ErrorInfo, type ReactNode } from "react"

import { registrarSenal } from "@/lib/bitacora"

/**
 * Lo que se ve cuando una pantalla se cae al dibujarse.
 *
 * ⚠ **Hasta ahora no había ninguno, y eso significa página en blanco.** Una
 * excepción durante el render desmonta el árbol entero: React deja el documento
 * vacío, la consola dice algo que nadie mira, y quien está delante ve blanco. No
 * hay mensaje, no hay forma de volver, y —lo peor para esto— no queda registro de
 * qué pasó. El reporte de esa clase de fallo llegaba como «se me puso todo blanco».
 *
 * ⚠ **Registra ANTES de pintar.** Si la señal se anotara en un efecto, el reporte
 * que alguien escriba desde aquí podría salir sin ella. Se hace en
 * `componentDidCatch`, que corre antes de que la persona llegue a pulsar nada.
 *
 * ⚠ **No ofrece «reintentar» reiniciando el estado.** Volver a montar el mismo árbol
 * con los mismos datos vuelve a caer, y encima parece que uno hizo algo mal al
 * insistir. Se ofrece recargar, que es lo único que de verdad cambia la situación.
 *
 * Tiene que ser una clase: React no expone los límites de error a los componentes
 * de función. Es la única del proyecto y ésa es la razón.
 */

type Props = { children: ReactNode }
type Estado = { caida: Error | null }

export class LimiteDeError extends Component<Props, Estado> {
  state: Estado = { caida: null }

  static getDerivedStateFromError(error: Error): Estado {
    return { caida: error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    registrarSenal({
      clase: "RENDER",
      nombre: error.name,
      mensaje: error.message,
      // La pila de COMPONENTES, no la de JavaScript: dice qué pantalla se cayó, que
      // es lo que dev necesita primero. La de JS ya viaja en el mensaje.
      detalles: { componentes: (info.componentStack ?? "").slice(0, 2000) },
    })
  }

  render(): ReactNode {
    if (this.state.caida === null) return this.props.children

    return (
      <div role="alert" className="mx-auto my-12 max-w-lg superficie p-6 text-center">
        <AlertTriangle
          size={28}
          aria-hidden={true}
          style={{ color: "var(--destructive)" }}
          className="mx-auto mb-3"
        />
        <h2 className="text-base font-semibold">Esta pantalla se cayó</h2>
        <p className="mt-2 text-sm" style={{ color: "var(--fg-muted)" }}>
          No es culpa tuya y no se perdió nada de lo que ya estaba guardado. El fallo quedó
          registrado: si lo reportas desde el botón de abajo a la derecha, llega a desarrollo con
          todo lo necesario para reproducirlo.
        </p>
        <p className="mt-3 font-mono text-xs" style={{ color: "var(--fg-muted)" }}>
          {this.state.caida.name}: {this.state.caida.message}
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="boton-primario mt-5 h-9 px-3 text-sm"
        >
          Recargar la página
        </button>
      </div>
    )
  }
}
